import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Download, MessageSquare, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import api, { API } from "@/lib/api";
import { toast } from "sonner";
import { eur } from "@/components/accounting/docUtils";

const fmtDT = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)} à ${iso.slice(11, 16).replace(":", "h")}` : "");
const fmtD = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "—");

export default function QuotePublic() {
  const { slug, token } = useParams();
  const [q, setQ] = useState(null);
  const [error, setError] = useState(false);
  const [dialog, setDialog] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [justValidated, setJustValidated] = useState(false);

  useEffect(() => {
    api.get(`/devis/${slug}/${token}`).then((r) => setQ(r.data)).catch(() => setError(true));
  }, [slug, token]);

  useEffect(() => {
    if (q?.client_name) document.title = `Devis ${q.number || ""} — ${q.client_name}`;
  }, [q]);

  const answer = async (action) => {
    if (action === "modification" && !message.trim()) {
      toast.error("Merci d'indiquer les modifications souhaitées");
      return;
    }
    setBusy(true);
    try {
      const r = await api.post(`/devis/${slug}/${token}/answer`, { action, message: message.trim() });
      setQ(r.data);
      setDialog(null);
      setMessage("");
      if (action === "valide") setJustValidated(true);
      if (action === "modification") toast.success("Votre demande de modification a été envoyée");
      if (action === "refuse") toast.success("Votre réponse a été transmise");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6">
        <div className="text-center">
          <p className="font-heading font-bold text-xl">Lien invalide</p>
          <p className="text-sm text-neutral-500 mt-1">Ce devis n'existe pas ou le lien est erroné.</p>
        </div>
      </div>
    );
  }
  if (!q) {
    return <div className="min-h-screen bg-neutral-50 flex items-center justify-center"><div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" /></div>;
  }

  const co = q.company || {};
  const pdfUrl = `${API}/devis/${slug}/${token}/pdf`;

  return (
    <div className="min-h-screen bg-neutral-100 py-8 px-4" data-testid="quote-public-page">
      <div className="max-w-3xl mx-auto space-y-5">
        {justValidated && (
          <div className="bg-green-600 text-white rounded-2xl p-6 text-center shadow-lg animate-fade-up" data-testid="quote-validated-success">
            <CheckCircle2 className="w-10 h-10 mx-auto mb-2" />
            <p className="font-heading font-bold text-lg">Votre devis a bien été validé.</p>
            <p className="text-green-100 text-sm mt-1">Merci pour votre confiance.</p>
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6 sm:p-10 space-y-8">
          <div className="flex justify-between gap-6 flex-wrap">
            <div className="text-sm space-y-0.5">
              <p className="font-heading font-bold text-lg" data-testid="quote-company-name">{co.company || "DIGITAL & GRAPHIC AGENCY"}</p>
              {co.address && <p className="text-neutral-500 whitespace-pre-line">{co.address}</p>}
              {(co.company_email || co.email) && <p className="text-neutral-500">{co.company_email || co.email}</p>}
              {co.phone && <p className="text-neutral-500">{co.phone}</p>}
              {co.siret && <p className="text-neutral-400 text-xs">SIRET : {co.siret}</p>}
              {co.tva_intra && <p className="text-neutral-400 text-xs">N° TVA : {co.tva_intra}</p>}
            </div>
            <div className="text-right">
              <p className="font-heading font-bold text-2xl" data-testid="quote-number">Devis {q.number ? `N° ${q.number}` : ""}</p>
              <p className="text-sm text-neutral-500">Émis le {fmtD(q.issue_date)}</p>
              <p className="text-sm text-neutral-500">Valable {q.validity_days} jours</p>
            </div>
          </div>

          <div className="bg-neutral-50 rounded-xl p-4 text-sm">
            <p className="text-[11px] uppercase tracking-wider text-neutral-400 mb-1">À l'attention de</p>
            <p className="font-semibold" data-testid="quote-client-name">{q.client_name}</p>
          </div>

          <div className="border border-neutral-200 rounded-xl overflow-hidden" data-testid="quote-items">
            <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 bg-neutral-50 text-[11px] uppercase tracking-wider text-neutral-400">
              <span className="col-span-7">Prestation / Désignation</span>
              <span className="col-span-1 text-right">Qté</span>
              <span className="col-span-2 text-right">Prix HT</span>
              <span className="col-span-2 text-right">Total HT</span>
            </div>
            {q.items.map((it, i) => (
              <div key={i} className="grid grid-cols-2 sm:grid-cols-12 gap-2 px-4 py-3 text-sm border-t border-neutral-100">
                <div className="col-span-2 sm:col-span-7 min-w-0">
                  <p className="font-medium whitespace-pre-line">{it.prestation}</p>
                  {it.description && <p className="text-xs text-neutral-400 whitespace-pre-line mt-0.5">{it.description}</p>}
                </div>
                <span className="sm:col-span-1 text-right text-neutral-500">{it.qty}</span>
                <span className="sm:col-span-2 text-right text-neutral-500">{eur(it.price_ht)}</span>
                <span className="sm:col-span-2 text-right font-medium">{eur(it.qty * it.price_ht)}</span>
              </div>
            ))}
          </div>

          <div className="flex justify-end">
            <div className="w-full sm:w-80 space-y-1.5 text-sm" data-testid="quote-totals">
              {q.discount_percent > 0 && (
                <>
                  <div className="flex justify-between"><span className="text-neutral-500">Montant initial HT</span><span>{eur(q.total_ht_brut)}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-500">Remise ({q.discount_percent} %)</span><span className="text-red-600">− {eur(q.discount_ht)}</span></div>
                </>
              )}
              <div className="flex justify-between"><span className="text-neutral-500">Total HT</span><span>{eur(q.total_ht)}</span></div>
              {q.tva_mode !== "non_assujetti" && (
                <div className="flex justify-between"><span className="text-neutral-500">TVA ({q.tva_rate} %)</span><span>{eur(q.total_tva)}</span></div>
              )}
              <div className="flex justify-between font-bold text-lg border-t border-neutral-200 pt-2">
                <span>Total {q.tva_mode === "non_assujetti" ? "" : "TTC"}</span>
                <span data-testid="quote-total">{eur(q.total_ttc)}</span>
              </div>
              {q.tva_mode === "non_assujetti" && <p className="text-[11px] text-neutral-400">TVA non applicable, art. 293 B du CGI</p>}
            </div>
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-sm" data-testid="quote-payment-terms">
            <p className="font-semibold mb-1">Conditions de paiement</p>
            {q.acompte_amount > 0 ? (
              <>
                <p>Acompte {q.acompte_type === "pourcent" ? `de ${q.acompte_value} %` : ""} : <strong>{eur(q.acompte_amount)}</strong></p>
                <p>Solde restant : <strong>{eur(q.solde)}</strong></p>
              </>
            ) : (
              <p>Montant total : <strong>{eur(q.total_ttc)}</strong> — Paiement à réception de la facture.</p>
            )}
            {q.delay && <p className="text-neutral-500 mt-1">Délai d'exécution : {q.delay}</p>}
          </div>

          {q.comment && (
            <p className="text-sm text-neutral-500 whitespace-pre-line" data-testid="quote-comment">{q.comment}</p>
          )}

          <div className="flex flex-wrap items-center gap-3 pt-2 border-t border-neutral-100">
            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="quote-download-pdf"
              className="inline-flex items-center gap-1.5 text-sm border border-neutral-200 rounded-lg px-4 py-2 hover:border-neutral-400 transition-colors">
              <Download className="w-4 h-4" /> Télécharger le PDF
            </a>
          </div>
        </div>

        {q.answerable && (
          <div className="bg-white rounded-2xl shadow-sm border border-neutral-200 p-6 space-y-3" data-testid="quote-actions-zone">
            <p className="font-heading font-bold">Votre réponse</p>
            <div className="grid sm:grid-cols-3 gap-2">
              <Button onClick={() => setDialog("valide")} className="bg-green-700 hover:bg-green-800 text-white gap-1.5" data-testid="quote-btn-validate">
                <CheckCircle2 className="w-4 h-4" /> Valider le devis
              </Button>
              <Button variant="outline" onClick={() => setDialog("modification")} className="gap-1.5" data-testid="quote-btn-modification">
                <MessageSquare className="w-4 h-4" /> Demander une modification
              </Button>
              <Button variant="outline" onClick={() => setDialog("refuse")} className="gap-1.5 text-red-600 border-red-200 hover:bg-red-50" data-testid="quote-btn-refuse">
                <XCircle className="w-4 h-4" /> Refuser le devis
              </Button>
            </div>
          </div>
        )}
        {q.status === "accepte" && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-sm text-green-800" data-testid="quote-status-valide">
            Ce devis a été validé le {fmtDT(q.validated_at)}.
          </div>
        )}
        {q.status === "refuse" && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5 text-sm text-red-800" data-testid="quote-status-refuse">
            Vous avez refusé ce devis le {fmtDT(q.refused_at)}.{q.refusal_reason ? ` Motif : ${q.refusal_reason}` : ""}
          </div>
        )}
        {q.status === "annule" && (
          <div className="bg-neutral-100 border border-neutral-200 rounded-2xl p-5 text-sm text-neutral-500" data-testid="quote-status-annule">
            Ce devis a été annulé.
          </div>
        )}
      </div>

      <Dialog open={dialog === "valide"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-md" data-testid="quote-confirm-validate" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold">Valider le devis {q.number ? `N° ${q.number}` : ""} ?</DialogTitle>
          <p className="text-sm text-neutral-600 mt-2">
            Confirmez-vous la validation définitive de ce devis de <strong>{eur(q.total_ttc)}</strong> ?
            Il sera alors verrouillé et une confirmation vous sera envoyée par e-mail.
          </p>
          <div className="flex gap-2 mt-4">
            <Button variant="outline" onClick={() => setDialog(null)} className="flex-1">Revenir</Button>
            <Button disabled={busy} onClick={() => answer("valide")} className="flex-1 bg-green-700 hover:bg-green-800 text-white" data-testid="quote-confirm-validate-btn">
              Confirmer la validation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "modification"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-md" data-testid="quote-modification-dialog" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold">Demander une modification</DialogTitle>
          <p className="text-sm text-neutral-600 mt-2">Merci d'indiquer les modifications souhaitées :</p>
          <Textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1" data-testid="quote-modification-message" />
          <Button disabled={busy || !message.trim()} onClick={() => answer("modification")}
            className="w-full mt-3 bg-neutral-900 hover:bg-neutral-800 text-white" data-testid="quote-modification-send">
            Envoyer ma demande
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog === "refuse"} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="max-w-md" data-testid="quote-refuse-dialog" aria-describedby={undefined}>
          <DialogTitle className="font-heading font-bold">Refuser le devis</DialogTitle>
          <p className="text-sm text-neutral-600 mt-2">Souhaitez-vous préciser la raison de votre refus ? (facultatif)</p>
          <Textarea rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1" data-testid="quote-refuse-message" />
          <div className="flex gap-2 mt-3">
            <Button variant="outline" onClick={() => setDialog(null)} className="flex-1">Revenir</Button>
            <Button disabled={busy} onClick={() => answer("refuse")} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="quote-refuse-confirm-btn">
              Confirmer le refus
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
